'use client'

import { useState, Suspense, useMemo, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useUser } from '@/hooks/useUser'
import { useLandlordData } from '@/hooks/useLandlordData'
import { LandlordTab } from '@/types/landlord'
import { Property } from '@/types'
import LandlordSidebar from '@/components/landlord/LandlordSidebar'
import PropertyCard from '@/components/landlord/PropertyCard'
import PropertyForm from '@/components/landlord/PropertyForm'
import ApplicationCard from '@/components/landlord/ApplicationCard'
import UserProfile from '@/components/landlord/UserProfile'
import PaymentManagement from '@/components/landlord/PaymentManagement'
import VerificationBadge from '@/components/landlord/VerificationBadge'
import CurrencySwitcher from '@/components/client/CurrencySwitcher'
import NotificationSystem from '@/components/client/NotificationSystem'
import NotificationPreferences from '@/components/NotificationPreferences'
import { Building2, FileText, CreditCard, TrendingUp } from 'lucide-react'
import OnboardingFlow from '@/components/OnboardingFlow'
import PropertyReviewsManagement from '@/components/landlord/PropertyReviewsManagement'
import TenanciesManagement from '@/components/landlord/TenanciesManagement'
import { useToast } from '@/hooks/useToast'

export const dynamic = 'force-dynamic'

function LandlordDashboardContent() {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout, refreshUser } = useAuth()
  const toast = useToast()

  const { user, loading: userLoading } = useUser()
 
  const searchParams = useMemo(() => {
    return new URLSearchParams(location.search)
  }, [location.search])

  const LANDLORD_TABS: LandlordTab[] = ['overview', 'properties', 'applications', 'payments', 'tenancies', 'reviews', 'profile', 'analytics', 'settings']
  const tabFromUrl = searchParams.get('tab')
  const propertyIdFromUrl = searchParams.get('propertyId')
  const initialTab: LandlordTab = (tabFromUrl && LANDLORD_TABS.includes(tabFromUrl as LandlordTab)) ? tabFromUrl as LandlordTab : 'overview'
  const initialPropertyId = (tabFromUrl === 'applications' && propertyIdFromUrl) ? propertyIdFromUrl : null

  // State for UI (restore tab from URL so refresh keeps you on the same tab)
  const [activeTab, setActiveTab] = useState<LandlordTab>(initialTab)
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(initialPropertyId)
  const [showPropertyForm, setShowPropertyForm] = useState(false)
  const [editingProperty, setEditingProperty] = useState<Property | null>(null)

  const pathname = location.pathname

  // Get data using custom hook
  const {
    properties,
    applications,
    tenancies,
    loading,
    error,
    stats,
    updateProperty,
    deleteProperty,
    refreshApplications,
    loadProperties,
    loadTenancies
  } = useLandlordData({ 
    userId: user?.id || '', 
    selectedPropertyId 
  })

  // Restore tab from URL when location changes (e.g. browser back/forward or refresh)
  useEffect(() => {
    const tab = searchParams.get('tab')
    const propertyId = searchParams.get('propertyId')
    if (tab && LANDLORD_TABS.includes(tab as LandlordTab)) {
      setActiveTab(tab as LandlordTab)
      if (tab === 'applications' && propertyId) {
        setSelectedPropertyId(propertyId)
      } else if (tab !== 'applications') {
        setSelectedPropertyId(null)
      }
    }
  }, [location.search])

  // ✅ ALWAYS before return
  useEffect(() => {
    if (!userLoading && (!user || user.role !== 'landlord')) {
      navigate('/auth', { replace: true })
    }
  }, [userLoading, user, navigate])

  // Auto-refresh properties when tab becomes active
  useEffect(() => {
    if (activeTab === 'properties' && user?.id) {
      loadProperties()
    }
  }, [activeTab, user?.id, loadProperties])

  // Periodic refresh of user data to catch verification status changes
  useEffect(() => {
    if (!user?.id) return
    
    const refreshInterval = setInterval(() => {
      refreshUser()
    }, 60000) // Refresh every 60 seconds
    
    return () => clearInterval(refreshInterval)
  }, [user?.id, refreshUser])

  // Refresh properties periodically when on properties tab
  useEffect(() => {
    if (activeTab === 'properties' && user?.id) {
      const refreshInterval = setInterval(() => {
        loadProperties()
      }, 120000) // Refresh every 2 minutes
      
      return () => clearInterval(refreshInterval)
    }
  }, [activeTab, user?.id, loadProperties])

  // Load tenancies when Tenancies tab is active
  useEffect(() => {
    if (activeTab === 'tenancies' && user?.id) {
      loadTenancies()
    }
  }, [activeTab, user?.id, loadTenancies])

  const updateUrlParams = (tab: LandlordTab, propertyId: string | null = null) => {
    try {
      const params = new URLSearchParams(searchParams.toString())
      params.set('tab', tab)

      if (propertyId) params.set('propertyId', propertyId)
      else params.delete('propertyId')

      const newUrl = `${pathname}?${params.toString()}`

      if (`${pathname}${location.search}` !== newUrl) {
        navigate(newUrl, { replace: true })
      }
    } catch {
      // Silently handle URL update errors
    }
  }

  // Handle tab changes
  const handleTabChange = (tab: LandlordTab) => {
    setActiveTab(tab)
    if (tab !== 'applications') {
      setSelectedPropertyId(null)
    }
    updateUrlParams(tab, tab === 'applications' ? selectedPropertyId : null)
  }

  // Handle property actions
  const handleAddProperty = () => {
    // Check if user is verified before allowing property creation
    if (!user?.is_verified) {
      toast.error('You must be verified by admin before you can list properties. Please complete your KYC verification first.')
      setActiveTab('profile')
      updateUrlParams('profile')
      return
    }
    setEditingProperty(null)
    setShowPropertyForm(true)
  }

  const handleEditProperty = (property: Property) => {
    setEditingProperty(property)
    setShowPropertyForm(true)
  }

  const handleDeleteProperty = async (propertyId: string) => {
    const { error } = await deleteProperty(propertyId)
      if (error) {
      console.error('Failed to delete property:', error)
    }
  }

  const handlePropertySuccess = async () => {
    setShowPropertyForm(false)
    setEditingProperty(null)
    // Refresh property list to show the newly created/updated property
    await loadProperties()
  }

  const handleToggleAvailability = async (propertyId: string, isAvailable: boolean) => {
    const { error } = await updateProperty(propertyId, { is_available: isAvailable })
    if (error) {
      console.error('Failed to update availability:', error)
      toast.error('Failed to update property availability')
    } else {
      toast.success(`Property ${isAvailable ? 'made available' : 'made unavailable'}`)
    }
  }

  const handleViewApplications = (propertyId: string) => {
    setSelectedPropertyId(propertyId)
    setActiveTab('applications')
    updateUrlParams('applications', propertyId)
  }

  const handleProfileUpdate = async () => {
    if (refreshUser) {
      await refreshUser()
    }
  }
  
  const handleLogout = () => {
    logout()
  }

  // Get user display name
  const getUserDisplayName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`
    }
    if (user?.kyc_data?.firstName && user?.kyc_data?.lastName) {
      return `${user.kyc_data.firstName} ${user.kyc_data.lastName}`
    }
    return user?.email || 'User'
  }

  // Loading state
  if (userLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-emerald-200 rounded-full"></div>
            <div className="w-16 h-16 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
          </div>
          <p className="mt-6 text-stone-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  
  return (
    <div className="min-h-screen bg-stone-50 flex">
      <OnboardingFlow userRole="landlord" />
      {/* Sidebar */}
      {user && (
        <LandlordSidebar
          activeTab={activeTab}
          onTabChange={handleTabChange}
          onLogout={handleLogout}
          user={user}
          stats={stats}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 lg:ml-0">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-stone-200 px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-display text-stone-900">Landlord Dashboard</h1>
              <p className="text-stone-600 mt-1">Manage your rental properties</p>
            </div>
            <div className="flex items-center space-x-4">
              {user?.id && <NotificationSystem userId={user.id} />}
              <CurrencySwitcher />
              {user && <VerificationBadge user={user} size="sm" />}
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="p-6">
          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="text-red-700 font-medium">{error}</p>
              </div>
            </div>
          )}

        {/* Property Form Modal */}
        {showPropertyForm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl">
              <PropertyForm
                property={editingProperty}
                onSuccess={handlePropertySuccess}
                onCancel={() => setShowPropertyForm(false)}
              />
            </div>
          </div>
        )}


        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Welcome Card */}
            <div className="bg-gradient-to-br from-emerald-600 to-emerald-800 rounded-2xl shadow-lg p-8 text-white relative overflow-hidden">
              {/* Decorative pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0 pattern-grid"></div>
              </div>
              
              <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <h3 className="text-2xl sm:text-3xl font-display mb-2">
                    Welcome back, {getUserDisplayName().split(' ')[0]}! 👋
                  </h3>
                  <p className="text-emerald-100 text-lg">
                    Here&apos;s what&apos;s happening with your properties today.
                  </p>
                </div>
                {!user?.is_verified ? (
                  <button
                    onClick={() => updateUrlParams('profile')}
                    className="bg-white/20 hover:bg-white/30 backdrop-blur text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg"
                  >
                    Complete Verification
                  </button>
                ) : (
                  <div className="bg-emerald-500/30 backdrop-blur px-6 py-3 rounded-xl border border-emerald-400/30">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="font-semibold">Profile Verified</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
                <p className="text-sm font-medium text-stone-600 mb-1">Total Properties</p>
                <p className="text-3xl font-display text-stone-900 mb-1">{stats.totalProperties}</p>
                <p className="text-xs text-stone-500">{stats.verifiedProperties} verified</p>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
                <p className="text-sm font-medium text-stone-600 mb-1">Pending Applications</p>
                <p className="text-3xl font-display text-stone-900 mb-1">{stats.pendingApplications}</p>
                <p className="text-xs text-stone-500">awaiting review</p>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
                <p className="text-sm font-medium text-stone-600 mb-1">Total Revenue</p>
                <p className="text-3xl font-display text-stone-900 mb-1">₦{stats.totalRevenue.toLocaleString()}</p>
                <p className="text-xs text-stone-500">all time</p>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
                <p className="text-sm font-medium text-stone-600 mb-1">Net Revenue</p>
                <p className="text-3xl font-display text-stone-900 mb-1">₦{(stats.netRevenue || 0).toLocaleString()}</p>
                <p className="text-xs text-stone-500">after commission</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div>
              <h2 className="text-xl sm:text-2xl font-display text-stone-900 mb-6">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <button 
                  onClick={handleAddProperty}
                  className="group bg-gradient-to-br from-emerald-600 to-emerald-700 text-white p-8 rounded-2xl hover:from-emerald-700 hover:to-emerald-800 transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl hover:shadow-emerald-500/25 text-left"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-4xl mb-3">🏠</div>
                      <h3 className="text-xl font-display mb-2">Add New Property</h3>
                      <p className="text-emerald-100">List your next rental property</p>
                    </div>
                    <svg className="w-6 h-6 opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </button>

                <button 
                  onClick={() => setActiveTab('applications')}
                  className="group bg-gradient-to-br from-amber-500 to-amber-600 text-white p-8 rounded-2xl hover:from-amber-600 hover:to-amber-700 transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl hover:shadow-amber-500/25 text-left"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-4xl mb-3">📋</div>
                      <h3 className="text-xl font-display mb-2">Review Applications</h3>
                      <p className="text-amber-100">Check pending tenant requests</p>
                    </div>
                    <svg className="w-6 h-6 opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </button>
              </div>
            </div>

            {/* Recent Activity */}
            <div>
              <h2 className="text-xl sm:text-2xl font-display text-stone-900 mb-6">Recent Activity</h2>
              <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
                {applications.length > 0 ? (
                  <div className="p-6">
                    <h3 className="font-semibold text-stone-900 mb-4">Latest Applications</h3>
                    <div className="space-y-3">
                      {applications.slice(0, 3).map((application) => (
                        <div key={application.id} className="flex items-center justify-between p-4 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors">
                          <div className="flex-1">
                            <p className="font-semibold text-stone-900">{application.property.title}</p>
                            <p className="text-sm text-stone-600">{application.client.email}</p>
                          </div>
                          <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                            application.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                            application.status === 'accepted' || application.status === 'approved' ? 'bg-emerald-100 text-emerald-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <div className="text-5xl mb-4">📭</div>
                    <p className="text-stone-600 font-medium mb-2">No recent activity</p>
                    <p className="text-sm text-stone-500">Start by adding your first property!</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Properties Tab */}
        {activeTab === 'properties' && (
          <div className="space-y-6" data-onboarding="properties-list">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-2xl font-display text-stone-900">My Properties</h2>
                <p className="text-stone-600 mt-1">Manage your rental listings</p>
              </div>
              <button
                onClick={handleAddProperty}
                className="bg-emerald-600 text-white px-6 py-3 rounded-xl hover:bg-emerald-700 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/25 font-semibold flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Property
              </button>
            </div>

              {loading.properties ? (
                <div className="text-center py-16">
                  <div className="relative inline-block">
                    <div className="w-12 h-12 border-4 border-emerald-200 rounded-full"></div>
                    <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
                  </div>
                  <p className="mt-4 text-stone-600 font-medium">Loading properties...</p>
                </div>
              ) : properties.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {properties.map((property) => (
                  <PropertyCard
                    key={property.id}
                    property={property}
                    onEdit={handleEditProperty}
                    onDelete={handleDeleteProperty}
                    onViewApplications={handleViewApplications}
                    onToggleAvailability={handleToggleAvailability}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-12 text-center">
                <div className="text-6xl mb-6">🏠</div>
                <h3 className="text-xl font-display text-stone-900 mb-2">No Properties Yet</h3>
                <p className="text-stone-600 mb-6 max-w-md mx-auto">
                  Start by adding your first rental property to begin receiving applications from verified tenants.
                </p>
                <button
                  onClick={handleAddProperty}
                  className="bg-emerald-600 text-white px-8 py-3 rounded-xl hover:bg-emerald-700 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/25 font-semibold"
                >
                  Add Your First Property
                </button>
              </div>
            )}
          </div>
        )}

        {/* Applications Tab */}
        {activeTab === 'applications' && (
          <div className="space-y-6" data-onboarding="applications-list">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-2xl font-display text-stone-900">
                  {selectedPropertyId ? 'Property Applications' : 'All Applications'}
                </h2>
                <p className="text-stone-600 mt-1">
                  {selectedPropertyId ? 'Applications for this property' : 'Review tenant applications'}
                </p>
              </div>
              {selectedPropertyId && (
                <button
                  onClick={() => {
                    setSelectedPropertyId(null)
                    updateUrlParams('applications', null)
                  }}
                  className="text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-2 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to All Applications
                </button>
              )}
            </div>

              {loading.applications ? (
                <div className="text-center py-16">
                  <div className="relative inline-block">
                    <div className="w-12 h-12 border-4 border-emerald-200 rounded-full"></div>
                    <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
                  </div>
                  <p className="mt-4 text-stone-600 font-medium">Loading applications...</p>
                </div>
              ) : applications.length > 0 ? (
              <div className="space-y-4">
                  {applications.map((application) => (
                    <ApplicationCard
                      key={application.id}
                      application={application}
                      onStatusUpdate={() => refreshApplications()}
                    />
                  ))}
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-12 text-center">
                <div className="text-6xl mb-6">📋</div>
                <h3 className="text-xl font-display text-stone-900 mb-2">No Applications Yet</h3>
                <p className="text-stone-600 max-w-md mx-auto">
                  When tenants apply to your properties, their applications will appear here for review.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-display text-stone-900">Payment Management</h2>
              <p className="text-stone-600 mt-1">Track rent payments and revenue</p>
            </div>
            {user && <PaymentManagement landlordId={user.id} />}
          </div>
        )}

        {/* Tenancies / Rent expiration Tab */}
        {activeTab === 'tenancies' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-display text-stone-900">Rent & Tenancies</h2>
              <p className="text-stone-600 mt-1">Track rent payment periods and expiration dates. Decide whether to renew when tenancy is about to expire.</p>
            </div>
            <TenanciesManagement
              tenancies={tenancies}
              loading={loading.tenancies ?? false}
              onRefresh={loadTenancies}
            />
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="space-y-6">
            {user && <PropertyReviewsManagement landlordId={user.id} />}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-6" data-onboarding="profile-tab">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-2xl font-display text-stone-900">Profile & Verification</h2>
                <p className="text-stone-600 mt-1">Manage your account and KYC status</p>
              </div>
              {user?.is_verified && (
                <div className="bg-emerald-50 border border-emerald-200 px-6 py-3 rounded-xl">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-semibold text-emerald-700">Profile Verified</span>
                  </div>
                </div>
              )}
            </div>
            {user && <UserProfile user={user} onUpdate={handleProfileUpdate} />}
          </div>
        )}

        {/* Notification Preferences / Settings Tab */}
        {activeTab === 'settings' && user ? (
          <div className="space-y-6">
            <NotificationPreferences userId={user.id} onUpdate={handleProfileUpdate} />
          </div>
        ) : null}
        </main>
      </div>
    </div>
  )
} 

export default function LandlordDashboard() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-stone-50 flex items-center justify-center">
          <div className="text-center">
            <div className="relative inline-block">
              <div className="w-16 h-16 border-4 border-emerald-200 rounded-full"></div>
              <div className="w-16 h-16 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
            </div>
            <p className="mt-6 text-stone-600 font-medium">Loading...</p>
          </div>
        </div>
      }
    >
      <LandlordDashboardContent />
    </Suspense>
  )
}
